<script setup lang="ts">
    interface MemberLoginProps {
        data: MemberLoginData;
    }

    const props = defineProps<MemberLoginProps>();

    const accountStore = useAccountStore();
    const { isLoggedIn } = storeToRefs(accountStore);
    const appStore = useAppStore();
    const { familyRoleOptions } = useAccount();

    const form = useTemplateRef<HTMLFormElement>('form');
    const { formValues, formErrors, checkFormValid, handleBlur } = useForm(form);

    const { t } = useI18n({
        useScope: 'local',
    });

    const handleSubmit = () => {
        if (!checkFormValid()) return;
        localStorage.setItem(MEMBER_DETAILS, JSON.stringify(formValues.value));
    };
</script>
<template>
    <mol-section
        class="org-member-login"
        :background-image="data.style.background_image"
        :background-image-mobile="data.style.background_image_mobile">
        <atm-container>
            <atm-grid class="relative z-1">
                <div class="col-span-2 flex flex-col items-center justify-center gap-5 md:col-span-4 lg:col-span-7 lg:gap-7">
                    <woom-icon-woom-logo class="hidden max-w-52.5 fill-woom-red md:block lg:max-w-100" />
                    <div class="w-full max-w-60 shrink max-lg:mx-auto lg:max-w-121.5">
                        <atm-member-logo />
                    </div>
                </div>
                <div class="col-span-2 flex flex-col justify-center md:col-span-8 lg:col-span-5">
                    <atm-account-card>
                        <template v-if="!isLoggedIn">
                            <div class="flex flex-col gap-2">
                                <div class="mb-2 flex flex-col gap-3 text-center">
                                    <atm-heading
                                        :font-bold="false"
                                        class="heading-member"
                                        size="3xl">
                                        {{ data.logged_out.title }}
                                    </atm-heading>
                                    <atm-heading
                                        level="span"
                                        size="lg"
                                        not-h1>
                                        {{ data.logged_out.subtitle }}
                                    </atm-heading>
                                    <p>{{ data.logged_out.description }}</p>
                                </div>

                                <form
                                    ref="form"
                                    method="get"
                                    :action="$helpers.generateLocalePath('/login')"
                                    @submit="handleSubmit">
                                    <mol-form-text
                                        v-model:text-value="formValues['firstName']"
                                        :label="$t('forms.firstName')"
                                        for-id="firstName"
                                        required
                                        :error="formErrors['firstName']"
                                        @blur="handleBlur" />
                                    <mol-form-text
                                        v-model:text-value="formValues['lastName']"
                                        :label="$t('forms.lastName')"
                                        for-id="lastName"
                                        :error="formErrors['lastName']"
                                        required
                                        @blur="handleBlur" />
                                    <mol-form-text
                                        v-model:text-value="formValues['email']"
                                        for-id="email"
                                        required
                                        type="email"
                                        :error="formErrors['email']"
                                        @blur="handleBlur"
                                        :label="$t('forms.email')" />

                                    <lazy-mol-form-select
                                        v-model:dropdown-value="formValues['familyRole']"
                                        for-id="familyRole"
                                        :label="$t('forms.familyRole.label')"
                                        :options="familyRoleOptions"
                                        label-background="bg-white"
                                        hide-overlay
                                        required />

                                    <div class="flex flex-col gap-2">
                                        <mol-form-checkbox
                                            v-model:checked-value="formValues['consent']"
                                            :caption="textLinks(data.form.legal_notice, appStore.getReference)"
                                            :label="data.form.opt_in_label"
                                            false-value="false"
                                            for-id="consent"
                                            true-value="true"
                                            no-margin
                                            required />
                                    </div>
                                    <atm-button
                                        class="mt-5"
                                        block
                                        styling="solid-secondary"
                                        type="submit">
                                        {{ data.form.button_register_label }}
                                    </atm-button>
                                </form>

                                <div class="text-center">{{ t('or') }}</div>
                                <a :href="$helpers.generateLocalePath('/login')">
                                    <atm-cta-style
                                        component="span"
                                        styling="outline"
                                        block>
                                        {{ data.form.button_login_label }}
                                    </atm-cta-style>
                                </a>
                            </div>
                        </template>

                        <template v-else>
                            <lazy-atm-link
                                to="/account"
                                block
                                styling="solid-secondary"
                                skip-local-path>
                                {{ data.logged_in.button_label }}
                            </lazy-atm-link>
                        </template>
                    </atm-account-card>
                </div>
            </atm-grid>
        </atm-container>
    </mol-section>
</template>
<i18n lang="json">
{
    "da": {
        "or": "eller"
    },
    "de": {
        "or": "Oder"
    },
    "en": {
        "or": "or"
    },
    "es": {
        "or": "o"
    },
    "fi": {
        "or": "tai"
    },
    "fr": {
        "or": "ou"
    },
    "nl": {
        "or": "of"
    },
    "pl": {
        "or": "albo"
    },
    "ro": {
        "or": "sau"
    },
    "sv": {
        "or": "eller"
    }
}
</i18n>
